import React, { useEffect, useContext } from 'react';
import { shape, arrayOf, string, number } from 'prop-types';
import styled from 'styled-components';
import { getImage } from 'gatsby-plugin-image';
import blogArticleProps from 'TMPropTypes/blogArticleProps';
import categoryProps from 'TMPropTypes/categoryProps';
import HeadTags from 'TMComponents/HeadTags';
import Main from 'TMComponents/Layout/Main';
import Input from 'TMComponents/form/Input';
import Canonical from 'TMComponents/Canonical';
import Pagination from 'TMComponents/Pagination';
import { BreadcrumbsContext } from 'TMComponents/Breadcrumbs';
import { BannerContext } from 'TMComponents/Banner';
import { LayoutContext } from 'TMComponents/Layout';
import BlogCard from 'TMComponents/BlogCard';
import Content from 'TMComponents/Content';
import { BodyReg } from 'TMComponents/Typography';
import { listingPath, toCanonicalHref } from 'TMUtils/blogPaths';
import listingMetaTags from 'TMUtils/listingMetaTags';
import { spacing } from 'TMDesignSystem/spacing';
import { breakpoints } from 'TMConstants';
import useBlogSearch from '../../hooks/useBlogSearch';
import useLegacyCategoryRedirect from '../../hooks/useLegacyCategoryRedirect';
import CategoryFilter from './_CategoryFilter';

const SITE_URL = 'https://www.thinkmoney.co.uk';

const PageContainer = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 ${spacing.medium};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        padding: 0 ${spacing.large};
    }
`;

const HeaderContent = styled.div`
    margin: ${spacing.small} 0 ${spacing.large} 0;
`;

const CategoryHeading = styled.h1`
    font-size: 32px;
    font-weight: 700;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: ${spacing.large} 0 0 0;
`;

const SectionTitle = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: ${({ theme }) => theme.colorSystem.UIText[1]};
    margin: ${spacing.xxLarge} 0 ${spacing.mediumLarge} 0;
`;

const CategoriesWrapper = styled.div`
    margin: ${spacing.large} 0;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${spacing.mediumLarge};
    margin: ${spacing.large} 0;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        grid-template-columns: repeat(2, 1fr);
        gap: ${spacing.mediumLarge};
    }

    @media (min-width: ${breakpoints.tabletDesktop}px) {
        grid-template-columns: repeat(3, 1fr);
        gap: ${spacing.mediumLarge};
    }
`;

const EmptyState = styled(BodyReg)`
    display: block;
    margin: ${spacing.large} 0 ${spacing.xxLarge} 0;
`;

/**
 * Blog > [category | News] > Page N, as deep as the listing goes. The
 * Breadcrumbs component prepends Home itself.
 */
const buildCrumbs = ({ variant, basePath, currentPage, categoryName }) => {
    const crumbs = [{ title: 'Blog', link: '/blog/' }];

    if (basePath !== '/blog/') {
        crumbs.push({
            title: variant === 'news' ? 'News' : categoryName,
            link: basePath
        });
    }

    if (currentPage > 1) {
        crumbs.push({
            title: `Page ${currentPage}`,
            link: listingPath(basePath, currentPage)
        });
    }

    return crumbs;
};

/**
 * Shared by the blog, news and category listing templates. Renders one
 * build-time page of articles plus crawlable pagination; only the free-text
 * search runs on the client, against the whole catalogue.
 */
const Listing = ({ data, pageContext, location }) => {
    const {
        variant,
        basePath,
        currentPage,
        numPages,
        categories,
        categoryName,
        categorySlug
    } = pageContext;

    const {
        query,
        searchedQuery,
        onSearchChange,
        isSearching,
        results,
        totalResults,
        maxResults
    } = useBlogSearch(data.allArticles.nodes, { categoryName });

    useLegacyCategoryRedirect(location.search, categories);

    const isBannerAllowed = true;
    const isCrumbsAllowed = true;

    const { setIsNavHidden } = useContext(LayoutContext);
    const { setShowBanner } = useContext(BannerContext);
    const { setCrumbs } = useContext(BreadcrumbsContext);

    useEffect(() => {
        setIsNavHidden(false);
        setShowBanner(isBannerAllowed);
        setCrumbs({
            show: isCrumbsAllowed,
            crumbList: buildCrumbs({
                variant,
                basePath,
                currentPage,
                categoryName
            })
        });
    }, []);

    const articles = isSearching ? results : data.allArticles.nodes;

    const content =
        variant === 'news' ? data.newsContent : data.pageContent;
    const metaTags = listingMetaTags(content.seoMetaTags.tags, {
        categoryName: variant === 'category' ? categoryName : undefined,
        currentPage,
        numPages
    });

    // `latestArticleText` is empty in the CMS today, so the page position has
    // to stand on its own rather than hang off a separator
    const sectionTitle = isSearching
        ? [
              'Search results',
              // the pills are hidden while searching, so name the category
              // the results are still scoped to
              categoryName ? ` in ${categoryName}` : ''
          ].join('')
        : [
              content.latestArticleText,
              currentPage > 1 ? `Page ${currentPage} of ${numPages}` : ''
          ]
              .filter(Boolean)
              .join(' – ');

    /**
     * Every page needs exactly one `h1`. Page 1 gets it from the CMS hero and a
     * category listing from its own heading, but a paginated blog or news page
     * has neither - so the section title stands in. Styling is unchanged; only
     * the element differs.
     */
    const sectionTitleTag =
        currentPage > 1 && variant !== 'category' ? 'h1' : 'h2';

    const paginationLabel = categoryName
        ? `${categoryName} pagination`
        : 'Blog pagination';

    const relLinks = [];
    if (currentPage > 1) {
        relLinks.push(
            <link
                key="prev"
                rel="prev"
                href={`${SITE_URL}${listingPath(
                    basePath,
                    currentPage - 1
                )}`}
            />
        );
    }
    if (currentPage < numPages) {
        relLinks.push(
            <link
                key="next"
                rel="next"
                href={`${SITE_URL}${listingPath(
                    basePath,
                    currentPage + 1
                )}`}
            />
        );
    }

    return (
        <Main
            isBannerAllowed={isBannerAllowed}
            isCrumbsAllowed={isCrumbsAllowed}
            reducedCrumbSpacing>
            <HeadTags tags={metaTags}>{relLinks}</HeadTags>
            <Canonical
                href={toCanonicalHref(listingPath(basePath, currentPage))}
            />
            <PageContainer>
                {currentPage === 1 && content.heroContent && (
                    <HeaderContent>
                        <Content
                            content={content.heroContent}
                            leftAlign
                            noSidePadding
                        />
                    </HeaderContent>
                )}

                {variant === 'category' && (
                    <CategoryHeading>{categoryName}</CategoryHeading>
                )}

                <Input
                    value={query}
                    onChange={onSearchChange}
                    placeholder={content.searchPlaceholder}
                    search
                />

                {sectionTitle && (
                    <SectionTitle as={sectionTitleTag}>
                        {sectionTitle}
                    </SectionTitle>
                )}

                {!isSearching && (
                    <CategoriesWrapper>
                        <CategoryFilter
                            categories={categories}
                            activeSlug={categorySlug}
                        />
                    </CategoriesWrapper>
                )}

                {isSearching && articles.length === 0 && (
                    <EmptyState>
                        {`Sorry, we couldn't find any articles matching "${searchedQuery}"${
                            categoryName ? ` in ${categoryName}` : ''
                        }.`}
                    </EmptyState>
                )}

                {!isSearching && articles.length === 0 && (
                    <EmptyState>
                        There are no articles here yet - please check back
                        soon.
                    </EmptyState>
                )}

                <CardsGrid>
                    {articles.map((article) => (
                        <BlogCard
                            key={article.id}
                            title={article.title}
                            slug={article.slug}
                            image={getImage(article.featuredImage)}
                            imageUrl={article.imageUrl}
                            excerpt={article.summary}
                            tags={article.categories.map(
                                (cat) => cat.name
                            )}
                            variant="default"
                        />
                    ))}
                </CardsGrid>

                {isSearching && totalResults > maxResults && (
                    <EmptyState>
                        {`Showing the first ${maxResults} of ${totalResults} matching articles - try a more specific search.`}
                    </EmptyState>
                )}

                {!isSearching && (
                    <Pagination
                        basePath={basePath}
                        currentPage={currentPage}
                        numPages={numPages}
                        label={paginationLabel}
                    />
                )}
            </PageContainer>
        </Main>
    );
};

Listing.propTypes = {
    data: shape({
        allArticles: shape({
            nodes: arrayOf(shape(blogArticleProps))
        })
    }).isRequired,
    pageContext: shape({
        variant: string,
        basePath: string,
        currentPage: number,
        numPages: number,
        categories: arrayOf(shape(categoryProps)),
        categoryName: string,
        categorySlug: string
    }).isRequired,
    location: shape({
        search: string
    }).isRequired
};

export default Listing;
